import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faDiscord,
  faInstagram,
  faTiktok,
  faTwitch,
  faTwitter,
  faYoutube,
  IconDefinition,
} from '@fortawesome/free-brands-svg-icons';
import css from './PartnerAboutSection.module.css';
import {
  Partner,
  PartnerSocial,
  PartnerStatistics,
} from '../../../PulsoidClient';
import moment from 'moment';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import classNames from 'classnames';
import {PartnerPreviewFavoriteWidget} from './PartnerPreviewFavorite/PartnerPreviewFavoriteWidget';
import {PartnerPreviewFavoriteFeature} from './PartnerPreviewFavorite/PratnerPreviewFavoriteFeature';

type PartnerAboutSectionProps = {
  partner?: Partner;
  statistics?: PartnerStatistics;
  isUserLoggedIn?: boolean;
};

enum ParnterSocialPlatform {
  DISCORD = 'discord',
  TWITTER = 'twitter',
  YOUTUBE = 'youtube',
  INSTAGRAM = 'instagram',
  TWITCH = 'twitch',
  TIKTOK = 'tiktok',
}

const partnerSocialMapping: Record<
  ParnterSocialPlatform,
  {icon: IconDefinition; urlPathName: keyof PartnerSocial; activeColor: string}
> = {
  [ParnterSocialPlatform.DISCORD]: {
    icon: faDiscord,
    urlPathName: 'discordUrl',
    activeColor: '#7289da',
  },
  [ParnterSocialPlatform.TWITTER]: {
    icon: faTwitter,
    urlPathName: 'twitterUrl',
    activeColor: '#1da1f2',
  },
  [ParnterSocialPlatform.YOUTUBE]: {
    icon: faYoutube,
    urlPathName: 'youtubeUrl',
    activeColor: '#ff0000',
  },
  [ParnterSocialPlatform.INSTAGRAM]: {
    icon: faInstagram,
    urlPathName: 'instagramUrl',
    activeColor: '#c32aa3',
  },
  [ParnterSocialPlatform.TWITCH]: {
    icon: faTwitch,
    urlPathName: 'twitchUrl',
    activeColor: '#9146ff',
  },
  [ParnterSocialPlatform.TIKTOK]: {
    icon: faTiktok,
    urlPathName: 'tiktokUrl',
    activeColor: '#69c9d0',
  },
};

const getJoinedTimeText = (joinedAt?: string) => {
  if (!joinedAt) {
    return '';
  }

  const joinedTime = moment(joinedAt).fromNow(true);
  const [joinedTimeNumber, joinedTimeUnit] = joinedTime.split(' ');

  return `${joinedTimeNumber}+ ${joinedTimeUnit} creating content with pulsoid`;
};

export const PartnerAboutSection = ({
  partner,
  statistics,
  isUserLoggedIn,
}: PartnerAboutSectionProps) => {
  return (
    <div className={css.root}>
      <div className={css.about}>
        <div className={css.partnerHeader}>
          <div className={css.partnerHeaderInfo}>
            <img
              className={css.partnerHeaderAvatar}
              src={partner?.avatarUrl}
              alt={partner?.name}
            />
            <div className={css.partnerHeaderInfoText}>
              <div className={css.partnerHeaderName}>{partner?.name}</div>
              <div className={css.partnerHeaderDescription}>
                {getJoinedTimeText(partner?.joinedAt)}
              </div>
            </div>
          </div>
          <div className={css.partnerHeaderSocials}>
            {Object.values(partnerSocialMapping).map(
              ({icon, urlPathName, activeColor}) =>
                partner?.social[urlPathName] ? (
                  <a
                    key={urlPathName}
                    className={css.partnerHeaderSocialButton}
                    href={partner.social[urlPathName]}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      color: activeColor,
                    }}
                  >
                    <FontAwesomeIcon size="lg" icon={icon as IconProp} />
                  </a>
                ) : null
            )}
          </div>
        </div>
        <div className={css.partnerFeedback}>
          <h4>My feedback</h4>

          <div className={css.partnerFeedbackStory}>
            <div className={css.dots}>
              <span className={css.dot}></span>
              <span className={css.dot}></span>
              <span className={css.dot}></span>
            </div>
            {partner?.feedback}
          </div>
        </div>
      </div>

      <div className={css.about}>
        <div className={css.pulseInfo}>
          <div
            className={classNames(
              css.pulseInfoItem,
              css.pulsoidInfoBroadcasted
            )}
          >
            <div className={css.pulseInfoItemValue}>
              {moment.duration(statistics?.broadcasted).asHours().toFixed(0)}
            </div>
            <div className={css.pulseInfoItemDescription}>
              Hours broadcasted
            </div>
          </div>

          <div
            className={classNames(css.pulseInfoItem, css.pulsoidInfoMaxHeart)}
          >
            <div className={css.pulseInfoItemValue}>{statistics?.max}</div>
            <div className={css.pulseInfoItemDescription}>
              Maximum heartrate
            </div>
          </div>

          <div
            className={classNames(css.pulseInfoItem, css.pulsoidInfoMinHeart)}
          >
            <div className={css.pulseInfoItemValue}>{statistics?.min}</div>
            <div className={css.pulseInfoItemDescription}>
              Minimum heartrate
            </div>
          </div>

          <div
            className={classNames(css.pulseInfoItem, css.pulsoidInfoAvgHeart)}
          >
            <div className={css.pulseInfoItemValue}>{statistics?.avg}</div>
            <div className={css.pulseInfoItemDescription}>
              Average heartrate
            </div>
          </div>
        </div>

        <h4>My favorite widgets</h4>
        <div className={css.favoriteWidgets}>
          {partner?.favoriteWidgets.map((widget) => (
            <PartnerPreviewFavoriteWidget
              widget={widget}
              isUserLoggedIn={isUserLoggedIn}
              key={widget.widgetId}
            />
          ))}
        </div>
        <h4>My favorite features</h4>
        <div className={css.favoriteFeatures}>
          {partner?.favoriteFeatures.map((feature) => (
            <PartnerPreviewFavoriteFeature
              key={feature.title}
              feature={feature}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
